<template>
  <div :class="[embedded ? 'map-kit-filter-panel map-kit-filter-panel--embedded' : 'map-kit-panel map-kit-panel--right map-kit-filter-panel']">
    <div v-if="!embedded" class="map-kit-panel-header">
      <span>{{ $t('filters.satellitesFilter') }}</span>
      <div class="map-kit-panel-header-icons">
        <Button
          icon="fas fa-xmark"
          size="small"
          text
          severity="secondary"
          @click="$emit('close')"
        />
      </div>
    </div>

    <div :class="embedded ? 'map-kit-filter-panel-body' : 'map-kit-panel-body'">
      <div class="map-kit-panel-section">
        <div v-if="sections.satellites" class="map-kit-collapse-body">
          <div class="map-kit-filter-description-row">
            <p class="map-kit-filter-description">{{ $t('filters.satellitesDescription') }}</p>
            <button type="button" class="map-kit-filter-select-all-link" @click="toggleAllLeo">
              {{ allLeo ? $t('filters.deselectAll') : $t('filters.selectAll') }}
            </button>
          </div>

          <div class="map-kit-filter-sat-group">
            <div class="map-kit-filter-sensor-group">
              <div class="map-kit-filter-checkbox-row map-kit-filter-sensor-group-header">
                <Checkbox
                  v-model="allOtSatellites"
                  input-id="sat-all-ot"
                  :binary="true"
                  @change="toggleAllOt"
                />
                <label for="sat-all-ot" class="map-kit-filter-orora-label">
                  <span>{{ $t('filters.ororaTechSafire') }}</span>
                  <img :src="ororaIconRed" alt="" class="map-kit-filter-orora-accent" />
                </label>
                <button
                  type="button"
                  class="map-kit-filter-expand-toggle"
                  @click="ororaConstellationExpanded = !ororaConstellationExpanded"
                >
                  <FontAwesomeIcon
                    :icon="ororaConstellationExpanded ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"
                  />
                  <span>{{ ororaConstellationExpanded ? $t('filters.showGrouped') : $t('filters.showIndividual') }}</span>
                </button>
              </div>
              <div
                class="map-kit-filter-ot-source-toggle"
                v-tooltip.top="$t('filters.scheduledOnlyNote')"
              >
                <SelectButton
                  v-model="otDataSourceFilter"
                  :options="otDataSourceOptions"
                  option-label="label"
                  option-value="value"
                  :allow-empty="false"
                />
              </div>
              <!-- Predicted swaths are fetched live across the whole servable range,
                   but scheduled tasking windows only exist for the near term, so the
                   toggle says so rather than looking broken when it returns nothing. -->
              <div
                v-if="otDataSourceFilter === 'scheduled'"
                class="map-kit-filter-sat-summary map-kit-filter-scheduled-note"
              >
                {{ $t('filters.scheduledOnlyNote') }}
              </div>
              <div v-if="!ororaConstellationExpanded" class="map-kit-filter-sat-summary">
                {{ $t('filters.selectedOfTotal', { selected: selectedOtSatelliteCount, total: otSatNames.length }) }}
              </div>
              <div v-if="ororaConstellationExpanded" class="map-kit-filter-sat-grid">
                <div v-for="sat in visibleOtLeoSatellites" :key="sat.name" class="map-kit-filter-checkbox-row map-kit-filter-sat-item">
                  <Checkbox
                    v-model="activeSatellites"
                    :input-id="'sat-' + sat.name"
                    :value="sat.name"
                    :disabled="sat.disabled"
                  />
                  <label :for="'sat-' + sat.name" :class="{ 'opacity-50': sat.disabled }">
                    {{ sat.label }}
                    <span v-if="sat.backup" class="map-kit-filter-sat-badge map-kit-filter-sat-badge--backup">{{ $t('filters.backup') }}</span>
                  </label>
                </div>
              </div>
            </div>

            <div v-for="group in sensorGroups" :key="group.key" class="map-kit-filter-sensor-group">
              <div class="map-kit-filter-checkbox-row map-kit-filter-sensor-group-header">
                <Checkbox
                  v-model="sensorGroupToggles[group.key]"
                  :input-id="'sat-all-' + group.key"
                  :binary="true"
                  @change="toggleSensorGroup(group.key)"
                />
                <label :for="'sat-all-' + group.key">{{ group.label }}</label>
              </div>
              <div class="map-kit-filter-sat-grid">
                <div v-for="sat in group.satellites" :key="sat.name" class="map-kit-filter-checkbox-row map-kit-filter-sat-item">
                  <Checkbox
                    v-model="activeSatellites"
                    :input-id="'sat-' + sat.name"
                    :value="sat.name"
                    :disabled="sat.disabled"
                  />
                  <label :for="'sat-' + sat.name" :class="{ 'opacity-50': sat.disabled }">
                    {{ sat.label }}
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import Button from 'primevue/button';
import Checkbox from 'primevue/checkbox';
import SelectButton from 'primevue/selectbutton';
import ororaIconRed from '@/assets/img/orora_icon_red.svg';

import { useFilterStore } from '@/composables/useFilterStore';
import { useAuth } from '@/composables/useAuth';

withDefaults(
  defineProps<{
    embedded?: boolean;
  }>(),
  {
    embedded: false,
  },
);

defineEmits<{ (e: 'close'): void }>();

const { selectedSatelliteNames, ororaConstellationExpanded, otDataSourceFilter } = useFilterStore();
const { isInternalUser } = useAuth();
const { t } = useI18n();

const otDataSourceOptions = computed(() => [
  { label: t('filters.predicted'), value: 'predicted' },
  { label: t('filters.scheduled'), value: 'scheduled' },
]);

const sections = reactive({
  satellites: true,
});

interface SatDef {
  name: string;
  label: string;
  disabled: boolean;
  retired?: boolean;
  backup?: boolean;
  internalOnly?: boolean;
  selectedByDefault?: boolean;
}

interface SensorGroup {
  key: string;
  label: string;
  satellites: SatDef[];
}

/**
 * Constellation checkboxes.
 *
 * The prediction service serves more of the fleet and the pipeline's
 * satellite_paths_metadata.json lists them, but most are not exposed in the product
 * yet. They are also excluded from every live query — see
 * NON_QUERYABLE_SATELLITE_NAMES in useFilterStore.ts, which is the list that actually
 * keeps them out of /api/swath, /api/coverage and /api/scheduled-swaths.
 */
const otLeoSatellites: SatDef[] = [
  {
    name: 'FOREST-2',
    label: 'Forest-2',
    disabled: false,
    internalOnly: true,
    selectedByDefault: false,
  },
  // { name: 'FOREST-3', label: 'Forest-3', disabled: false },
  { name: 'FOREST-4', label: 'Forest-4P', disabled: false },
  { name: 'FOREST-5', label: 'Forest-5P', disabled: false },
  { name: 'FOREST-6', label: 'Forest-6P', disabled: false },
  { name: 'FOREST-7', label: 'Forest-7P', disabled: false },
  { name: 'FOREST-8', label: 'Forest-8P', disabled: false },
  { name: 'FOREST-9', label: 'Forest-9P', disabled: false },
  { name: 'FOREST-10', label: 'Forest-10P', disabled: false },
  { name: 'FOREST-11', label: 'Forest-11P', disabled: false },
  // { name: 'FOREST-12', label: 'Forest-12', disabled: false },
  // { name: 'FOREST-13', label: 'Forest-13', disabled: false },
  // { name: 'FOREST-14', label: 'Forest-14', disabled: false },
  // { name: 'FOREST-15', label: 'Forest-15', disabled: false },
  // { name: 'FOREST-16', label: 'Forest-16', disabled: false },
  // { name: 'FOREST-17', label: 'Forest-17', disabled: false },
  // { name: 'FOREST-18', label: 'Forest-18', disabled: false },
  // { name: 'FOREST-19', label: 'Forest-19', disabled: false },
  // { name: 'GENA-OT', label: 'Gena-OT', disabled: false },
];

const visibleOtLeoSatellites = computed(() =>
  otLeoSatellites.filter((satellite) => !satellite.internalOnly || isInternalUser.value),
);

const sensorGroups: SensorGroup[] = [
  {
    key: 'viirs',
    label: 'VIIRS',
    satellites: [
      { name: 'NOAA 20 (JPSS-1)', label: 'NOAA-20', disabled: false },
      { name: 'NOAA 21 (JPSS-2)', label: 'NOAA-21', disabled: false },
      { name: 'SUOMI NPP', label: 'Suomi NPP', disabled: false },
    ],
  },
  {
    key: 'modis',
    label: 'MODIS',
    satellites: [
      { name: 'AQUA', label: 'Aqua', disabled: false },
      { name: 'TERRA', label: 'Terra', disabled: false },
    ],
  },
  {
    key: 'avhrr',
    label: 'AVHRR',
    satellites: [
      { name: 'METOP-B', label: 'MetOp-B', disabled: false },
      { name: 'METOP-C', label: 'MetOp-C', disabled: false },
    ],
  },
  {
    key: 'slstr',
    label: 'SLSTR',
    satellites: [
      { name: 'SENTINEL-3A', label: 'Sentinel-3A', disabled: false },
      { name: 'SENTINEL-3B', label: 'Sentinel-3B', disabled: false },
    ],
  },
  {
    key: 'msi',
    label: 'MSI',
    satellites: [
      { name: 'SENTINEL-2A', label: 'Sentinel-2A', disabled: false },
      { name: 'SENTINEL-2B', label: 'Sentinel-2B', disabled: false },
      { name: 'SENTINEL-2C', label: 'Sentinel-2C', disabled: false },
    ],
  },
  {
    key: 'oli',
    label: 'OLI',
    satellites: [
      { name: 'LANDSAT 8', label: 'Landsat-8', disabled: false },
      { name: 'LANDSAT 9', label: 'Landsat-9', disabled: false },
    ],
  },
  {
    key: 'mersi',
    label: 'MERSI',
    satellites: [
      { name: 'FENGYUN 3D', label: 'FengYun-3D', disabled: false },
      { name: 'FENGYUN 3E', label: 'FengYun-3E', disabled: false },
      { name: 'FENGYUN 3F', label: 'FengYun-3F', disabled: false },
    ],
  },
  {
    key: 'sgli',
    label: 'SGLI',
    satellites: [
      { name: 'GCOM-C', label: 'GCOM-C', disabled: false },
    ],
  },
  {
    key: 'tropomi',
    label: 'TROPOMI',
    satellites: [
      { name: 'SENTINEL-5P', label: 'Sentinel-5P', disabled: false },
    ],
  },
];

const allSensorSatellites = sensorGroups.flatMap((group) => group.satellites);

function allEnabledSatellites(): string[] {
  return [...visibleOtLeoSatellites.value, ...allSensorSatellites]
    .filter((satellite) => !satellite.disabled && satellite.selectedByDefault !== false)
    .map((satellite) => satellite.name);
}

const otSatNames = computed(() =>
  visibleOtLeoSatellites.value.filter((satellite) => !satellite.disabled).map((satellite) => satellite.name),
);
const internalOnlySatelliteNames = new Set(
  otLeoSatellites.filter((satellite) => satellite.internalOnly).map((satellite) => satellite.name.toUpperCase()),
);

function initialSatelliteSelection(): string[] {
  if (!selectedSatelliteNames.value.length) return allEnabledSatellites();
  const accessibleSelection = selectedSatelliteNames.value.filter(
    (name) => isInternalUser.value || !internalOnlySatelliteNames.has(name.toUpperCase()),
  );
  return accessibleSelection.length ? accessibleSelection : allEnabledSatellites();
}

const activeSatellites = ref<string[]>(initialSatelliteSelection());
const selectedOtSatelliteCount = computed(() =>
  otSatNames.value.filter((name) => activeSatellites.value.includes(name)).length,
);

const allLeo = ref(false);
const allOtSatellites = ref(true);
const sensorGroupToggles = reactive<Record<string, boolean>>(
  Object.fromEntries(sensorGroups.map((group) => [group.key, false])),
);

function toggleAllLeo() {
  const state = !allLeo.value;
  allLeo.value = state;
  if (state) {
    toggleGroupSatellites([...visibleOtLeoSatellites.value, ...allSensorSatellites], true);
  } else {
    // Clear outright rather than removing the names this panel happens to know.
    // The selection is restored from localStorage, so it can hold satellites that
    // have no checkbox here — subtracting only the known ones left those behind and
    // "deselect all" visibly did not deselect all.
    activeSatellites.value = [];
  }
  allOtSatellites.value = state;
  for (const group of sensorGroups) {
    sensorGroupToggles[group.key] = state;
  }
}

function toggleAllOt() {
  toggleGroupSatellites(visibleOtLeoSatellites.value, allOtSatellites.value);
}

function toggleSensorGroup(key: string) {
  const group = sensorGroups.find((sensorGroup) => sensorGroup.key === key);
  if (group) toggleGroupSatellites(group.satellites, sensorGroupToggles[key]);
}

function toggleGroupSatellites(satellites: SatDef[], state: boolean) {
  for (const satellite of satellites) {
    if (satellite.disabled) continue;
    const idx = activeSatellites.value.indexOf(satellite.name);
    if (state && idx === -1) activeSatellites.value.push(satellite.name);
    if (!state && idx !== -1) activeSatellites.value.splice(idx, 1);
  }
}

function syncGroupToggles() {
  const enabledOt = visibleOtLeoSatellites.value
    .filter((satellite) => !satellite.disabled)
    .map((satellite) => satellite.name);
  allOtSatellites.value = enabledOt.every((name) => activeSatellites.value.includes(name));

  let allSensorGroupsSelected = true;
  for (const group of sensorGroups) {
    const enabled = group.satellites.filter((satellite) => !satellite.disabled).map((satellite) => satellite.name);
    const allSelected = enabled.length > 0 && enabled.every((name) => activeSatellites.value.includes(name));
    sensorGroupToggles[group.key] = allSelected;
    if (!allSelected) allSensorGroupsSelected = false;
  }

  allLeo.value = allOtSatellites.value && allSensorGroupsSelected;
}

watch(activeSatellites, (names) => {
  selectedSatelliteNames.value = [...names];
  syncGroupToggles();
}, { immediate: true, deep: true, flush: 'sync' });
</script>

<style scoped lang="scss">
.map-kit-filter-panel--embedded {
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
  background: transparent;
}

.map-kit-filter-panel-body {
  flex: 1;
  overflow: auto;
  min-height: 0;
  padding: 0.7rem 0.75rem 1rem;
}

.map-kit-filter-panel--embedded :deep(.map-kit-panel-section) {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
}

.map-kit-filter-panel--embedded :deep(.map-kit-filter-description-row) {
  align-items: flex-start;
  gap: 0.75rem;
  margin-bottom: 0.1rem;
}

.map-kit-filter-panel--embedded :deep(.map-kit-filter-description) {
  margin: 0;
  color: var(--p-text-muted-color, #8da0a7);
  font-size: 0.78rem;
  line-height: 1.45;
}

.map-kit-filter-panel--embedded :deep(.map-kit-filter-select-all-link) {
  font-size: 0.76rem;
  font-weight: 600;
}

.map-kit-filter-panel--embedded :deep(.map-kit-filter-sat-group) {
  gap: 0.7rem;
  margin-bottom: 0;
}

.map-kit-filter-panel--embedded :deep(.map-kit-filter-sensor-group) {
  margin-bottom: 0;
  padding: 0.8rem 0.85rem;
  border-radius: 14px;
  border: 1px solid rgba(250, 249, 247, 0.08);
  background: rgba(250, 249, 247, 0.035);
  box-shadow: inset 0 1px 0 rgba(250, 249, 247, 0.03);
}

.map-kit-filter-panel--embedded :deep(.map-kit-filter-sensor-group-header) {
  margin-bottom: 0;
}

.map-kit-filter-panel--embedded :deep(.map-kit-filter-checkbox-row) {
  margin-bottom: 0;
  font-size: 0.84rem;
}

.map-kit-filter-panel--embedded :deep(.map-kit-filter-expand-toggle) {
  border-radius: 999px;
  border-color: rgba(250, 249, 247, 0.12);
  background: rgba(250, 249, 247, 0.05);
  color: var(--p-text-muted-color, #8da0a7);
  padding: 0.28rem 0.55rem;
}

.map-kit-filter-panel--embedded :deep(.map-kit-filter-ot-source-toggle) {
  padding-left: 1.55rem;
  margin-top: 0.5rem;
}

.map-kit-filter-panel--embedded :deep(.map-kit-filter-ot-source-toggle .p-selectbutton) {
  background: rgba(250, 249, 247, 0.03);
  border-radius: 999px;
  padding: 0.12rem;
}

.map-kit-filter-panel--embedded :deep(.map-kit-filter-ot-source-toggle .p-togglebutton) {
  border-radius: 999px;
}

.map-kit-filter-panel--embedded :deep(.map-kit-filter-sat-summary) {
  padding-left: 1.55rem;
  margin-top: 0.45rem;
  font-size: 0.76rem;
}

.map-kit-filter-panel--embedded :deep(.map-kit-filter-sat-grid) {
  gap: 0.32rem 0.7rem;
  padding-left: 1.55rem;
  margin-top: 0.5rem;
}

.map-kit-filter-panel--embedded :deep(.map-kit-filter-sat-item) {
  padding: 0.2rem 0;
}

:root:not(.color-scheme-dark) .map-kit-filter-panel--embedded :deep(.map-kit-filter-sensor-group) {
  border-color: rgba(15, 23, 42, 0.12);
  background: rgba(15, 23, 42, 0.03);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.55);
}

:root:not(.color-scheme-dark) .map-kit-filter-panel--embedded :deep(.map-kit-filter-expand-toggle) {
  border-color: rgba(15, 23, 42, 0.12);
  background: rgba(15, 23, 42, 0.05);
  color: #475569;
}
</style>
